import { AsyncThunkConfig, createSelector, EntityId, EntityState } from "@reduxjs/toolkit";

import { RootState, ThunkExtraArg } from "..";

export interface AsyncEntityThunkConfig<RejectedValue> extends AsyncThunkConfig {
  extra: ThunkExtraArg;
  state: RootState;
  rejectedValue: RejectedValue;
}

export interface AsyncEntityState<T, Id extends EntityId> extends EntityState<T, Id> {
  status: "idle" | "loading" | "succeeded" | "failed";
  loadingIds: Id[];
  lastFetchedById: Record<Id, number>;
  enqueueIds: Id[];
  pages: Record<string, Id[]>;
  pageMetadata: Record<string, { totalCount: number; pageSize: number; totalPages?: number; }>;
  paginationStatus: Record<string, "idle"|"loading"|"succeeded"|"failed">;
}

export function createAsyncEntityInitialState<T, Id extends EntityId>(initialEntityState: EntityState<T,Id>): AsyncEntityState<T, Id> {
  return {
    ...initialEntityState,
    status: "idle",
    loadingIds: [],
    lastFetchedById: {} as Record<Id, number>,
    enqueueIds: [],
    pages: {},
    pageMetadata: {},
    paginationStatus: {}
  }
}

export function createForeignKeySelector<T, Id extends EntityId, SliceState extends AsyncEntityState<T, Id>, ForeignKeyType extends EntityId >(selectSlice: (state: RootState) => SliceState, foreignKeyProp: keyof T) {
  const selectEntityByForeignKey = createSelector(
    selectSlice,
    (_, foreignKey: ForeignKeyType) => foreignKey,
    (sliceState: SliceState, foreignKey: ForeignKeyType) => {
      const entities = sliceState?.entities;
      if (entities === undefined) {
        return [];
      }
      return (Object.values(entities) as T[]).filter((entity: T) => entity[foreignKeyProp] === foreignKey)
    } 
  )
  const selectEntitiesByForeignKey = createSelector(
    selectSlice,
    (_, foreignKey: ForeignKeyType[]) => foreignKey,
    (sliceState: SliceState, foreignKey: ForeignKeyType[]) => {
      const entities = sliceState?.entities;
      if (entities === undefined) {
        return [];
      }
      return foreignKey.flatMap((fk) => (Object.values(entities) as T[]).filter((entity: T) => entity[foreignKeyProp] === fk)).filter(Boolean)
    }
  )
  
  return {selectEntitiesByForeignKey, selectEntityByForeignKey}
}